
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  userSignup,
  clearSignupSuccess,
  fetchAllUsersForDropdown,
} from "../../redux/usersSlice";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
  Grid,
  IconButton,
  Paper,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Captcha from "./Captcha";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import ConfirmationDialog from "../Common/ConfirmationQuitFormModal";

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨   DESIGN TOKENS                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#2F4E8B";
const OUTLINE    = "#D4D9E0";
const LABEL_GREY = "#4F5565";

/* ───  TextField “standard” underline-only with custom focus colors          */
const CustomTextField = styled(TextField)(() => ({
  "& .MuiInput-underline:before": {
    borderBottomColor: OUTLINE,
  },
  "& .MuiInput-underline:hover:before": {
    borderBottomColor: NAVY_LIGHT,
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: NAVY,
  },
  "& .MuiFormLabel-root": {
    color: LABEL_GREY,
    fontSize: 14,
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: NAVY,
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
    padding: "6px 0",
  },
}));

// Make GradientPaper ref-forwarding for Modal compatibility
const GradientPaper = React.forwardRef(function GradientPaper({ children, sx, ...rest }, ref) {
  return (
    <Paper
      ref={ref}
      elevation={8}
      sx={{
        borderRadius: 3,
        p: { xs: 3, md: 4 },
        width: { xs: "100%", md: 820 },
        mx: "auto",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  );
});

const SignUp = ({ handleClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, signupSuccess, currentUser, allUsers } = useSelector(
    (state) => state.users
  );

  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const availableRoles = [
    { value: "ROLE_ADMIN", label: "Admin" },
    { value: "ROLE_COLLABORATOR", label: "Collaborator" },
    { value: "ROLE_USER", label: "User" },
  ];

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Veuillez entrer une adresse email valide.")
      .required("Adresse email est requise."),
    username: Yup.string().required("Nom d'utilisateur est requis."),
    fullName: Yup.string(),
    address: Yup.string(),
    phone: Yup.string().required("Numéro de téléphone est requis."),
    birthDate: Yup.date()
      .max(
        new Date(new Date().setFullYear(new Date().getFullYear() - 20)),
        "Vous devez avoir au moins 20 ans."
      )
      .required("Date de naissance est requise."),
    password: Yup.string()
      .min(8, "Le mot de passe doit contenir au moins 8 caractères.")
      .matches(/[A-Z]/, "Doit contenir au moins une lettre majuscule.")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Doit contenir au moins un caractère spécial."
      )
      .matches(/\d/, "Doit contenir au moins un chiffre.")
      .test(
        "no-email-username-fullname",
        "Ne doit pas contenir votre email, nom d'utilisateur ou nom complet.",
        function (value) {
          const { email, username, fullName } = this.parent;
          return (
            !value.includes(email) &&
            !value.includes(username) &&
            (!fullName || !value.includes(fullName))
          );
        }
      )
      .required("Mot de passe est requis."),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Les mots de passe doivent correspondre.")
      .required("Confirmation du mot de passe est requise."),
  });

  useEffect(() => {
    if (currentUser && allUsers.length === 0) {
      dispatch(fetchAllUsersForDropdown());
    }
  }, [dispatch, allUsers.length, currentUser]);

  useEffect(() => {
    if (signupSuccess || error) {
      const t = setTimeout(() => dispatch(clearSignupSuccess()), 5000);
      return () => clearTimeout(t);
    }
  }, [signupSuccess, error, dispatch]);

  useEffect(() => {
    if (signupSuccess) {
      const isAdmin =
        currentUser &&
        (currentUser.roles.includes("ROLE_ADMIN") ||
          currentUser.roles.includes("ROLE_SUPERADMIN"));
      if (!currentUser || !isAdmin) {
        const t = setTimeout(() => navigate("/connexion"), 3000);
        return () => clearTimeout(t);
      }
    }
  }, [signupSuccess, navigate, currentUser]);

  const handleFormSubmit = (values = {}) => {
    setFormValues(values);
    setCaptchaOpen(true);
  };

  const handleCaptchaSuccess = (captchaVerification) => {
    setCaptchaOpen(false);
    const payload = {
      ...formValues,
      phone: `${formValues.countryCode || ""}${formValues.phone}`,
      captchaVerification,
    };
    if (currentUser) {
      payload.roles = formValues.roles ? [formValues.roles] : [];
      payload.companyId = formValues.companyId || "";
    } else {
      payload.roles = null;
    }
    dispatch(userSignup(payload));
  };

  const cancel = () => (dirty ? setDialogOpen(true) : handleClose());
  const closeDialog = (confirm) => {
    setDialogOpen(false);
    if (confirm) handleClose();
  };

  // Helper to always return a simple error message for signup
  const getErrorMessage = (error) => {
    if (!error) return "";
    // Always show the same message for any error
    return "Cette adresse email ou nom d'utilisateur existe déjà";
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <GradientPaper
        sx={{
          position: "relative",
          border: `2px solid ${OUTLINE}`,
        }}
      >
        <IconButton
          onClick={cancel}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: LABEL_GREY,
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          component="h1"
          variant="h5"
          sx={{
            color: NAVY,
            textAlign: "center",
            mb: 3,
            fontWeight: 700,
          }}
        >
          Inscription
        </Typography>

        <Formik
          initialValues={{
            email: "",
            username: "",
            fullName: "",
            address: "",
            phone: "",
            birthDate: "",
            password: "",
            confirmPassword: "",
            companyId: "",
            roles: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form style={{ width: "100%" }} onChange={() => setDirty(true)}>
              {/* Ligne 1 */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    required
                    id="email"
                    name="email"
                    label="Adresse Email"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    required
                    id="username"
                    name="username"
                    label="Nom d'utilisateur"
                    value={values.username}
                    onChange={handleChange}
                    error={touched.username && Boolean(errors.username)}
                    helperText={touched.username && errors.username}
                  />
                </Grid>
              </Grid>

              {/* Ligne 2 */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    id="fullName"
                    name="fullName"
                    label="Nom Prénom"
                    value={values.fullName}
                    onChange={handleChange}
                    error={touched.fullName && Boolean(errors.fullName)}
                    helperText={touched.fullName && errors.fullName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    id="address"
                    name="address"
                    label="Adresse"
                    value={values.address}
                    onChange={handleChange}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>
              </Grid>

              {/* Ligne 3 */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <PhoneInput
                    country={"fr"}
                    value={values.phone}
                    onChange={(val, country) => {
                      setFieldValue("phone", val);
                      setFieldValue("countryCode", country.dialCode);
                    }}
                    inputStyle={{
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                      borderRadius: 8,
                      borderColor: OUTLINE,
                      height: 56,
                    }}
                    inputProps={{ name: "phone", required: true }}
                  />
                  {touched.phone && errors.phone && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.phone}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    id="birthDate"
                    name="birthDate"
                    label="Date de naissance"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={values.birthDate}
                    onChange={handleChange}
                    error={touched.birthDate && Boolean(errors.birthDate)}
                    helperText={touched.birthDate && errors.birthDate}
                  />
                </Grid>
              </Grid>

              {/* Company/Rôles si admin */}
              {currentUser && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={CustomTextField}
                      variant="standard"
                      fullWidth
                      select
                      id="companyId"
                      name="companyId"
                      label="Choisir une société"
                      value={values.companyId}
                      onChange={handleChange}
                    >
                      <MenuItem value="">-</MenuItem>
                      {allUsers
                        .filter((u) => u.roles.includes("ROLE_ADMIN"))
                        .map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.fullName}
                          </MenuItem>
                        ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="standard">
                      <InputLabel id="roles-label">Rôles</InputLabel>
                      <Select
                        labelId="roles-label"
                        id="roles"
                        name="roles"
                        value={values.roles}
                        label="Rôles"
                        onChange={(e) => setFieldValue("roles", e.target.value)}
                        sx={{ "& .MuiSelect-select": { color: NAVY } }}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {/* Passwords */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    required
                    id="password"
                    name="password"
                    label="Mot de Passe"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={CustomTextField}
                    variant="standard"
                    fullWidth
                    required
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirmer le Mot de Passe"
                    type="password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    error={
                      touched.confirmPassword && Boolean(errors.confirmPassword)
                    }
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                    }
                  />
                </Grid>
              </Grid>

              {/* Feedback */}
              <Box sx={{ mt: 2 }}>
                {signupSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {signupSuccess}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {getErrorMessage(error)}
                  </Alert>
                )}
              </Box>

              {/* Submit */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: NAVY,
                    borderRadius: 1,
                    textTransform: "none",
                    px: 5,
                    fontWeight: "bold",
                    "&:hover": { backgroundColor: NAVY_LIGHT },
                  }}
                >
                  {loading ? "Inscription en cours..." : "Créer un Compte"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </GradientPaper>

      <ConfirmationDialog
        open={dialogOpen}
        title="Quitter le formulaire"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        onClose={closeDialog}
      />

      <Modal
        open={captchaOpen}
        onClose={() => {}}
        BackdropProps={{ onClick: (e) => e.stopPropagation() }}
      >
        <Box
          sx={{
            position: "absolute",
            top: { xs: 0, sm: "50%" },
            left: { xs: 0, sm: "50%" },
            transform: { xs: "none", sm: "translate(-50%, -50%)" },
            width: { xs: "100vw", sm: "1100px", md: "1400px", lg: "1500px" },
            maxWidth: { xs: "100vw", sm: "99vw" },
            minWidth: { xs: "100vw", sm: "1100px", md: "1400px", lg: "1500px" },
            height: { xs: "100vh", sm: "95vh" },
            maxHeight: { xs: "100vh", sm: "97vh" },
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: "background.paper",
            borderRadius: { xs: 0, sm: 2 },
            boxShadow: 24,
            p: { xs: 0, sm: 4 },
            border: `2px solid #2F4E8B`,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#2F4E8B",
              borderRadius: "4px",
            },
            scrollbarColor: "#2F4E8B #e6e8f0",
            scrollbarWidth: "thin",
          }}
        >
          <Captcha
            onSuccess={handleCaptchaSuccess}
            handleClose={() => setCaptchaOpen(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default SignUp;
